import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {ErrorService} from "../service/ErrorService";

declare global {
    interface Window {
        __appNavigate?: (path: string, state?: any) => void;
        __appNavigateToError?: (state?: any) => void;
    }
}

export default function NavigatorBridge() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let lastNavAt = 0;
        const MIN_GAP = 800;

        const navigateSafe = (path: string, state?: any) => {
            const now = Date.now();

            if (now - lastNavAt < MIN_GAP) return;
            lastNavAt = now;

            if (location.pathname === "/error" && path === "/error") return;

            navigate(path, { replace: true, state });
        };

        const sendErrorLog = (error: Error) => {
            const path = window.location.pathname;
            const verificationId = path.split("/")[1];

            console.log("NavigatorBridge sending error:", error.message);

            ErrorService.getInstance().sendErrorLog(error, path, verificationId);
        };

        window.__appNavigate = (path: string, state?: any) => {
            navigateSafe(path, state);
        };

        window.__appNavigateToError = (state?: any) => {
            navigateSafe("/error", state);
        };

        const onError = (event: ErrorEvent) => {
            const error =
                event.error instanceof Error ? event.error : new Error(event.message);

            sendErrorLog(error);

            window.__appNavigateToError?.({
                reason: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        };

        const onRejection = (event: PromiseRejectionEvent) => {
            const reason =
                (event.reason && event.reason.message) || String(event.reason);
            window.__appNavigateToError?.({ reason });
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);

        return () => {
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onRejection);

            // cleanup globals only if they still point to our functions
            if (window.__appNavigate) {
                delete window.__appNavigate;
            }
            if (window.__appNavigateToError) {
                delete window.__appNavigateToError;
            }
        };
    }, [navigate, location.pathname]);

    return null;
}